// navigation-panel.tsx
import React, { useMemo } from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
} from '@mui/material';

import { NavigationSection } from './types/types';

import { useScrollSpyWithNavigation } from '@/features/properties/hooks/use-scroll-spy-with-navigation';

/** Shared layout: sticky Paper with header and list container. */
export const NavigationPanelLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Paper
    elevation={2}
    sx={{ position: 'sticky', top: 20, maxHeight: 'calc(100vh - 40px)', overflow: 'auto' }}
  >
    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant='h6' fontWeight={600}>
        入力項目
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        各セクションをクリックして移動
      </Typography>
    </Box>

    <List sx={{ p: 0 }}>{children}</List>
  </Paper>
);

export interface NavigationPanelItemProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children?: React.ReactNode;
}

/** Shared list item: same styling for active state, icon, label. Optional children (e.g. Chip). */
export const NavigationPanelItem: React.FC<NavigationPanelItemProps> = ({
  label,
  isActive,
  onClick,
  icon,
  children,
}) => (
  <ListItem disablePadding>
    <ListItemButton
      onClick={onClick}
      selected={isActive}
      sx={{
        py: 1.5,
        px: 2,
        '&.Mui-selected': {
          backgroundColor: '#D7E2FF',
          borderRight: 3,
          borderRightColor: '#0D2F60',
          color: '#0D2F60',
          '&:hover': { backgroundColor: '#D7E2FF' },
        },
      }}
    >
      <ListItemIcon sx={{ color: isActive ? '#0D2F60' : 'text.secondary', minWidth: 40 }}>
        {icon}
      </ListItemIcon>

      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontWeight: isActive ? 600 : 400,
          color: isActive ? '#0D2F60' : 'text.primary',
          fontSize: '0.9rem',
        }}
      />

      {children}
    </ListItemButton>
  </ListItem>
);

interface NavigationPanelProps {
  sections: NavigationSection[];
}

export const NavigationPanelCommon: React.FC<NavigationPanelProps> = ({ sections }) => {
  const [activeSection, setActiveSection] = React.useState<string>(sections[0]?.id || '');

  const HEADER_OFFSET = 50;

  const scrollSpyOptions = useMemo(
    () => ({ topOffset: HEADER_OFFSET, bottomOffsetRatio: 0.1 }),
    [HEADER_OFFSET]
  );

  const { handleSectionClick } = useScrollSpyWithNavigation(
    sections,
    activeSection,
    setActiveSection,
    scrollSpyOptions
  );

  const getIcon = (sectionId: string) => {
    const section = sections.find((sec) => sec.id === sectionId);
    return section?.icon || null;
  };

  return (
    <NavigationPanelLayout>
      {sections.map((section) => (
        <NavigationPanelItem
          key={section.id}
          label={section.label}
          isActive={activeSection === section.id}
          onClick={() => handleSectionClick(section.id)}
          icon={getIcon(section.id)}
        />
      ))}
    </NavigationPanelLayout>
  );
};
